import React, { FC, useCallback, useMemo, useState } from 'react'
import {
  Box,
  Stack,
  TableCell,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import {
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import { PharmacySelector } from '@/apps/latest/components/portal/PharmacySelector'
import {
  Banner,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FilterRow,
  NotionButton,
  NotionCheckbox,
  NotionDatePicker,
  NotionPagination,
  NotionTextField,
  PageLoading,
  TableToolbar,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import {
  useMetricsRecordsQuery,
  usePharmacyListQuery,
  usePharmacyUrlParam,
} from '@/features/internal/metrics'
import { useExportMetricsRecordsMutation } from '@/features/internal'
import {
  MetricsExportRequest,
  MetricsFilters,
  MetricsSortField,
  MetricsSortOptions,
} from '@/features/internal/metrics/types/metrics'
import dayjs from 'dayjs'

const METRICS_CONFIG = {
  PAGE_SIZE: 20,
  PRESCRIPTION_NUMBER_DISPLAY_LENGTH: 6,
} as const

const EMPTY_FILTERS: MetricsFilters = {
  search: '',
  startDate: '',
  endDate: '',
  minMedicationCount: null,
  maxMedicationCount: null,
  hasAudio: null,
  hasSoap: null,
  soapCopied: null,
}

// 明細タブ: 旧・指導実績一覧ページの内容をそのままタブ化したもの (PageScaffoldは親の統計ページが持つ)。
// 店舗フィルタは `pharmacy_id` URLパラメータ経由で概況・比較と共有される。
export const RecordsTab: FC = () => {
  const navigate = useNavigate()
  const { pharmacyId: selectedPharmacyId, updatePharmacyParam } =
    usePharmacyUrlParam()
  const [currentPage, setCurrentPage] = useState(1)
  const exportMutation = useExportMetricsRecordsMutation()

  const [tempFilters, setTempFilters] = useState<MetricsFilters>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] =
    useState<MetricsFilters>(EMPTY_FILTERS)

  const [sort, setSort] = useState<MetricsSortOptions>({
    field: 'created_at',
    direction: 'desc',
  })

  const { pharmacyData } = usePharmacyListQuery()

  const formatDateTime = useCallback((dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    try {
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return '-'
      return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
    } catch {
      return '-'
    }
  }, [])

  const formatDuration = useCallback((seconds: number | null | undefined) => {
    if (seconds == null || seconds === 0) return '-'
    const total = Math.round(seconds)
    return `${Math.floor(total / 60)}分${total % 60}秒`
  }, [])

  const queryParams = useMemo(() => {
    if (!pharmacyData) return null

    const dateRange =
      appliedFilters.startDate || appliedFilters.endDate
        ? {
            start: appliedFilters.startDate || '2020-01-01',
            end:
              appliedFilters.endDate || new Date().toISOString().split('T')[0],
          }
        : undefined

    return {
      page: currentPage,
      page_size: METRICS_CONFIG.PAGE_SIZE,
      ...(dateRange && { date_range: dateRange }),
      ...(appliedFilters.search && { search: appliedFilters.search }),
      filters: {
        ...(appliedFilters.hasAudio !== null && {
          has_audio: appliedFilters.hasAudio,
        }),
        ...(appliedFilters.hasSoap !== null && {
          has_soap: appliedFilters.hasSoap,
        }),
        ...(appliedFilters.soapCopied !== null && {
          soap_copied: appliedFilters.soapCopied,
        }),
        ...(appliedFilters.minMedicationCount && {
          medication_count_min: appliedFilters.minMedicationCount,
        }),
        ...(appliedFilters.maxMedicationCount && {
          medication_count_max: appliedFilters.maxMedicationCount,
        }),
      },
      sort: {
        field: sort.field,
        order: sort.direction,
      },
      // 未選択は「絞らない」= キーを送らない。本部なら全社、それ以外は自店になる
      pharmacy_ids:
        selectedPharmacyId && selectedPharmacyId !== ''
          ? [selectedPharmacyId]
          : undefined,
    }
  }, [pharmacyData, currentPage, sort, appliedFilters, selectedPharmacyId])

  const {
    data: recordsData,
    isLoading,
    error: queryError,
    refetch,
  } = useMetricsRecordsQuery({
    params: queryParams!,
    enabled: !!queryParams,
  })

  const metrics = recordsData?.records ?? []
  const totalCount = recordsData?.total ?? 0
  const loading = isLoading || exportMutation.isPending
  const error = queryError?.message ?? null

  const handleSearch = () => {
    setAppliedFilters(tempFilters)
    setCurrentPage(1)
  }

  const handleReset = () => {
    setTempFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setCurrentPage(1)
  }

  const handleExportCSV = async () => {
    const filters = appliedFilters
    const dateRange =
      filters.startDate || filters.endDate
        ? {
            start: filters.startDate || '2020-01-01',
            end: filters.endDate || new Date().toISOString().split('T')[0],
          }
        : undefined

    const exportParams: MetricsExportRequest = {
      format: 'csv' as const,
      ...(dateRange && { date_range: dateRange }),
      ...(filters.search && { search: filters.search }),
      filters: {
        ...(filters.hasAudio !== null && { has_audio: filters.hasAudio }),
        ...(filters.hasSoap !== null && { has_soap: filters.hasSoap }),
        ...(filters.soapCopied !== null && {
          soap_copied: filters.soapCopied,
        }),
        ...(filters.minMedicationCount && {
          medication_count_min: filters.minMedicationCount,
        }),
        ...(filters.maxMedicationCount && {
          medication_count_max: filters.maxMedicationCount,
        }),
      },
    }

    if (selectedPharmacyId && selectedPharmacyId !== '') {
      exportParams.pharmacy_ids = [selectedPharmacyId]
    }

    await exportMutation.mutateAsync(exportParams)
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
  }

  const handleSortChange = (field: MetricsSortField) => {
    const isSameField = sort.field === field
    const newDirection =
      isSameField && sort.direction === 'desc' ? 'asc' : 'desc'
    setSort({ field, direction: newDirection })
    setCurrentPage(1)
  }

  const sortLabel = (field: MetricsSortField, label: string) => (
    <TableSortLabel
      active={sort.field === field}
      direction={sort.field === field ? sort.direction : 'desc'}
      onClick={() => handleSortChange(field)}
    >
      {label}
    </TableSortLabel>
  )

  const columns: DataTableColumn[] = [
    { key: 'created_at', label: sortLabel('created_at', '日時') },
    { key: 'pharmacy_name', label: sortLabel('pharmacy_name', '薬局') },
    {
      key: 'medical_institution',
      label: sortLabel('medical_institution', '医療機関'),
    },
    {
      key: 'prescription_number',
      label: sortLabel('prescription_number', '処方箋'),
    },
    {
      key: 'medication_count',
      label: sortLabel('medication_count', '指導情報'),
    },
    { key: 'status', label: '状態' },
    { key: 'detail', label: '', align: 'right' },
  ]

  // 旧ページのPageScaffold meta (「全 N 件」) はタブ化に伴いtoolbar captionへ合流
  const rangeCaption =
    totalCount > 0
      ? `${(currentPage - 1) * METRICS_CONFIG.PAGE_SIZE + 1}-${Math.min(
          currentPage * METRICS_CONFIG.PAGE_SIZE,
          totalCount
        )} 件 / 全 ${totalCount.toLocaleString()} 件`
      : '0 件'

  return (
    <Stack gap={3}>
      {loading && <PageLoading />}
      {error && <Banner severity="danger">{error}</Banner>}

      <FilterRow>
        <Box sx={{ minWidth: 200 }}>
          <PharmacySelector
            value={selectedPharmacyId}
            onChange={(value) => {
              updatePharmacyParam(value)
              setCurrentPage(1)
            }}
            pharmacyData={pharmacyData}
            size="small"
            label="店舗"
            showAllOption={true}
          />
        </Box>
        <NotionDatePicker
          label="開始日"
          value={tempFilters.startDate ? dayjs(tempFilters.startDate) : null}
          onChange={(date) =>
            setTempFilters({
              ...tempFilters,
              startDate: date ? date.format('YYYY-MM-DD') : '',
            })
          }
        />
        <NotionDatePicker
          label="終了日"
          value={tempFilters.endDate ? dayjs(tempFilters.endDate) : null}
          onChange={(date) =>
            setTempFilters({
              ...tempFilters,
              endDate: date ? date.format('YYYY-MM-DD') : '',
            })
          }
        />
        <Box sx={{ width: 280 }}>
          <NotionTextField
            label="医療機関 / 処方箋番号"
            value={tempFilters.search}
            onChange={(e) =>
              setTempFilters({ ...tempFilters, search: e.target.value })
            }
            InputProps={{ endAdornment: <SearchIcon /> }}
          />
        </Box>
        <Box sx={{ width: 120 }}>
          <NotionTextField
            type="number"
            label="薬剤数(以上)"
            value={tempFilters.minMedicationCount || ''}
            onChange={(e) =>
              setTempFilters({
                ...tempFilters,
                minMedicationCount: e.target.value
                  ? parseInt(e.target.value)
                  : null,
              })
            }
          />
        </Box>
        <Box sx={{ width: 120 }}>
          <NotionTextField
            type="number"
            label="薬剤数(以下)"
            value={tempFilters.maxMedicationCount || ''}
            onChange={(e) =>
              setTempFilters({
                ...tempFilters,
                maxMedicationCount: e.target.value
                  ? parseInt(e.target.value)
                  : null,
              })
            }
          />
        </Box>
      </FilterRow>

      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <NotionCheckbox
          checked={tempFilters.hasAudio === true}
          onChange={(e) =>
            setTempFilters({
              ...tempFilters,
              hasAudio: e.target.checked ? true : null,
            })
          }
          label="音声録音あり"
        />
        <NotionCheckbox
          checked={tempFilters.hasSoap === true}
          onChange={(e) =>
            setTempFilters({
              ...tempFilters,
              hasSoap: e.target.checked ? true : null,
            })
          }
          label="SOAP生成済み"
        />
        <NotionCheckbox
          checked={tempFilters.soapCopied === true}
          onChange={(e) =>
            setTempFilters({
              ...tempFilters,
              soapCopied: e.target.checked ? true : null,
            })
          }
          label="SOAPコピー済"
        />
        <Box sx={{ flex: 1 }} />
        <NotionButton
          variant="secondary"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          検索
        </NotionButton>
        <NotionButton variant="ghost" onClick={handleReset}>
          リセット
        </NotionButton>
      </Stack>

      <TableToolbar
        caption={rangeCaption}
        actions={
          <>
            <NotionButton
              variant="ghost"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={loading}
            >
              更新
            </NotionButton>
            <NotionButton
              variant="ghost"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={loading}
            >
              CSV出力
            </NotionButton>
          </>
        }
      />

      {metrics.length === 0 ? (
        <EmptyState title="データが見つかりませんでした" />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <DataTable columns={columns}>
            {metrics.map((metric) => (
              <TableRow key={metric.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatDateTime(metric.created_at)}
                </TableCell>
                <TableCell
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metric.pharmacy_name}
                </TableCell>
                <TableCell
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metric.medical_institution}
                </TableCell>
                <TableCell>
                  {metric.prescription_number
                    ? `${metric.prescription_number.slice(
                        0,
                        METRICS_CONFIG.PRESCRIPTION_NUMBER_DISPLAY_LENGTH
                      )}...`
                    : '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {[
                    metric.medication_count != null
                      ? `${metric.medication_count}剤`
                      : null,
                    metric.has_audio
                      ? formatDuration(metric.duration_seconds)
                      : null,
                    metric.exchange_count != null && metric.exchange_count > 0
                      ? `${metric.exchange_count}回`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '-'}
                </TableCell>
                <TableCell>
                  <Typography sx={PORTAL_TEXT.cellSecondary}>
                    {metric.soap_copied
                      ? 'コピー済み'
                      : metric.has_soap
                      ? 'SOAP生成済み'
                      : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <NotionButton
                    variant="ghost"
                    onClick={() =>
                      navigate(`/portal/metrics/records/${metric.id}`)
                    }
                  >
                    詳細
                  </NotionButton>
                </TableCell>
              </TableRow>
            ))}
          </DataTable>
        </Box>
      )}

      {totalCount > METRICS_CONFIG.PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <NotionPagination
            count={Math.ceil(totalCount / METRICS_CONFIG.PAGE_SIZE)}
            page={currentPage}
            onChange={handlePageChange}
          />
        </Box>
      )}
    </Stack>
  )
}
